import { getAbTestablePages, CustomPaginator } from "../../helpers";
import DevicePicker from "../reusables/DevicePicker/DevicePicker";
import "./AbTestStyles.css";
import { useEffect, useState, useContext, useRef } from "react";
import { SelectedProjectContext } from "../../contexts";
import { Spinner } from "react-bootstrap";
import LazyLoad from "react-lazyload";
import StoryPage from "../FunnelAnalysis/StoryPage";
import config from "../../config";
import PopperTooltip from "../reusables/PopperTooltip/PopperTooltip";
import { useDraggable } from "react-use-draggable-scroll";
import { toast } from "react-toastify";

const AbTestStep1 = ({ setSelectedTest, setActiveTab, activeSelectors }) => {
  const selectedProject = useContext(SelectedProjectContext);

  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [device, setDevice] = useState("desktop");
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const [tooltipContent, setTooltipContent] = useState(<>Empty tooltip</>);
  const [tooltipCoords, setTooltipCoords] = useState({
    x: 0,
    y: 0,
    visible: false,
  });

  const ref = useRef(null);
  const { events } = useDraggable(ref, {
    isMounted: true,
  });

  const heatmapPageWidth = config.HEATMAP_PAGE_WIDTH;
  const storyPageWidth = heatmapPageWidth + config.STORY_PAGE_OFFSET;

  const scrollToPage = (page) => {
    const elementId = `heatmap${page}`;
    const element = document.getElementById(elementId);
    const container = document.getElementById("story-scroll-container");
    if (element && container) {
      const targetScrollLeft = element.offsetLeft - container.offsetLeft;
      container.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (event.target.tagName.toLowerCase() !== "button") {
        setMouseX(event.pageX);
        setMouseY(event.pageY);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  useEffect(() => {
    function calculateCurrentPage(scrollLeft) {
      return Math.floor((scrollLeft + config.HEATMAP_OFFSET) / storyPageWidth);
    }
    const handleScroll = () => {
      if (ref.current && ref.current.scrollLeft) {
        const currentPageIndex = calculateCurrentPage(ref.current.scrollLeft);
        setPage(currentPageIndex);
      }
    };

    const currentRef = ref.current;
    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedProject || !device) return;
    setLoading(true);
    getAbTestablePages({
      projectId: selectedProject.value,
      device: device,
    }).then((res) => {
      if (res && res.length) {
        setPages(res);
        setTotalPages(res.length);
      } else {
        setPages([]);
        setTotalPages(0);
      }
      setLoading(false);
    });
  }, [selectedProject, device]);

  function setSelectedElement(element) {
    if (activeSelectors.includes(element.jsSelector)) {
      toast.error(
        "The same element cannot have more than one active A/B test.",
      );
      return;
    }
    const type = element.tag === "img" ? "simple_img" : "simple_text";
    const jsSelector = element.jsSelector;
    const url = element.url;
    const screenshotOriginal = element.oldSrc;
    setSelectedTest({
      element,
      type,
      jsSelector,
      url,
      screenshotOriginal,
      device,
    });
    setActiveTab(2);
  }

  return (
    <div>
      <div style={{ width: "669px" }} className="abtest-info-block mb-32">
        Select an element you want to A/B test. We recommend avoiding dynamic
        elements.
      </div>
      <div className="fc-black fw-700 mb-16">Devices</div>
      <div className="space-between">
        <DevicePicker device={device} setDevice={setDevice} marginTop="0px" />
        <div
          className="mr--100"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            userSelect: "none",
          }}
        >
          {CustomPaginator(page, scrollToPage, totalPages)}
        </div>
      </div>

      <div
        ref={ref}
        id="story-scroll-container"
        {...events}
        className="no-select hide-scrollbar heatmap-container flex overflow-x-scroll"
        style={{
          paddingBottom: "100px",
          width: `calc(100% + ${config.AB_SELECTOR_WIDTH}px)`,
          minHeight: `1000px`,
          paddingRight: `calc(100% - ${config.AB_SELECTOR_PADDING}px)`,
        }}
      >
        <PopperTooltip
          coordinates={tooltipCoords}
          tooltip={tooltipContent}
          containerRef={ref}
          popperClassName=""
        />

        {loading ? (
          <div className="mb-32">
            <Spinner style={{ height: "44px", width: "44px" }} />
          </div>
        ) : pages && pages.length > 0 ? (
          <>
            {pages.map((page, i) => (
              <LazyLoad
                key={i}
                scrollContainer="#story-scroll-container"
                overflow={true}
              >
                <StoryPage
                  pageCount={1}
                  page={page}
                  i={i}
                  deviceType={device}
                  containerRef={ref}
                  mouseX={mouseX}
                  mouseY={mouseY}
                  blockMouse={false}
                  setModalShow={() => {}}
                  setSelectedElement={setSelectedElement}
                  setTooltipCoords={setTooltipCoords}
                  setTooltipContent={setTooltipContent}
                  setGoalName={() => {}}
                  setStoryState={() => {}}
                  setStoryStartPage={() => {}}
                  storyState={1}
                  setSavedScroll={() => {}}
                  isAbTest={true}
                  showAllElements={false}
                />
              </LazyLoad>
            ))}
          </>
        ) : (
          <h3>There were no results for this device</h3>
        )}
      </div>
    </div>
  );
};

export default AbTestStep1;
